// src/utils/badgeColors.js
// Single source of truth for the status / priority / update / assignment
// badge colours that were previously copy-pasted across Task, Report,
// TestingTask, DevelopmentTask, Deployments and TaskAssignment.

// Normalise legacy "Completed" → "Complete" before colour lookup.
const norm = (v) => String(v || "").trim().toLowerCase();

// --- Task / report status -> solid colour (text or dot) --------------------
export const STATUS_COLORS = {
  "in progress": "#0d6efd",
  complete: "#198754",
  completed: "#198754",
  "on hold": "#ffc107",
  "not started": "#6c757d",
  "not possible": "#d32f2f",
  testing: "#6f42c1",
};

export const getStatusColor = (status) => STATUS_COLORS[norm(status)] || "#000";

// --- Status -> { bg, text } ------------------------------------------------
// The tinted form, for a filled cell or chip rather than a dot. Every status
// tbl_Queries actually holds is here — Complete, Not Started, Not Possible,
// On Hold, In Progress, Testing — so nothing falls through to grey by
// accident. Backgrounds are deliberately pale: these sit behind body text on
// a printed report, where a saturated fill makes the text unreadable and
// costs a fortune in toner.
export const getStatusColors = (value) => {
  const v = norm(value);
  if (v === "complete" || v === "completed")
    return { bg: "#e8f5e9", text: "#1b5e20" };
  if (v === "in progress") return { bg: "#e3f2fd", text: "#0d47a1" };
  if (v === "on hold") return { bg: "#fff8e1", text: "#e65100" };
  if (v === "not possible") return { bg: "#ffebee", text: "#b71c1c" };
  if (v === "testing" || v === "testing in progress")
    return { bg: "#ede7f6", text: "#4527a0" };
  if (v === "testing on hold") return { bg: "#fce4ec", text: "#880e4f" };
  return { bg: "#f1f3f5", text: "#495057" }; // Not Started, and anything new
};

// --- Query type -> { bg, text } --------------------------------------------
// Three values in the data: BUG, New Task, Rework. A bug reads as the thing
// to look at first, so it gets the warm colour and the other two stay calm —
// the point of colouring this column at all is to find the bugs in a page of
// two hundred rows.
export const getQueryTypeColors = (value) => {
  const v = norm(value);
  if (v === "bug") return { bg: "#ffebee", text: "#b71c1c" };
  if (v === "rework") return { bg: "#fff3e0", text: "#e65100" };
  if (v === "new task") return { bg: "#e3f2fd", text: "#0d47a1" };
  return { bg: "transparent", text: "inherit" };
};

// --- Yes / No -> { bg, text } ----------------------------------------------
// For the Manual and Support columns. Only "Yes" is tinted: a column that is
// 90% No would otherwise be a solid block of colour saying nothing, and the
// exception is the whole reason anybody reads it.
export const getYesNoColors = (value) => {
  const v = norm(value);
  if (v === "yes" || v === "true" || v === "1")
    return { bg: "#e8f5e9", text: "#1b5e20" };
  return { bg: "transparent", text: "inherit" };
};

// --- Any repeated name -> a stable pale tint -------------------------------
// Raised By holds client codes (SRPPLSTORE, KPTGATE, TPNPM...) — an open set,
// so there is no fixed palette to look them up in. Hashing the name gives the
// same client the same tint on every page and every reprint, which is what
// makes a team-wise report scannable: the eye groups the bands without
// reading a single code.
//
// Fixed saturation and lightness rather than a free HSL: it keeps every tint
// pale enough to read black text on, which a hashed lightness would not.
//
// Returned as hex, not as an hsl() string, because these colours are also read
// by the PDF export, and jsPDF takes RGB numbers — one format that both the
// stylesheet and the exporter understand beats converting at each call site.
const hslToHex = (h, s, l) => {
  const a = (s / 100) * Math.min(l / 100, 1 - l / 100);
  const channel = (n) => {
    const k = (n + h / 30) % 12;
    const v = l / 100 - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(255 * v)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${channel(0)}${channel(8)}${channel(4)}`;
};

/* WCAG relative luminance, and the contrast ratio between two hex colours.
   Only defined for hex — every colour in this file is hex, and a caller that
   passes something else gets `null` rather than a wrong answer. */
const hexToRgb = (hex) => {
  const m = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(String(hex || ""));
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null;
};

const luminance = ([r, g, b]) => {
  const f = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};

export const contrastRatio = (hexA, hexB) => {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  if (!a || !b) return null;
  const la = luminance(a);
  const lb = luminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
};

/* Keep the colour, guarantee the reading.

   These pairs were drawn for badges: a short word, bold, at 12px on a screen,
   where amber-on-cream carries fine. The printed report puts them behind
   9.5px body text on paper, and several fall well below the 4.5:1 that needs
   — "Medium" priority is 2.5:1, which on a laser printer is a smudge.

   The background is the signal, so the background is what is kept; the text
   drops to near-black only where the pair would not otherwise be readable.
   Doing it by measurement rather than by hand-picking the offenders means a
   later palette edit cannot quietly reintroduce an unreadable cell. */
export const readableOn = (colors, minRatio = 4.5) => {
  if (!colors?.bg || colors.bg === "transparent") return colors;
  const ratio = contrastRatio(colors.bg, colors.text);
  if (ratio === null || ratio >= minRatio) return colors;
  return { ...colors, text: "#1a1d20" };
};

export const getNameTint = (value) => {
  const v = norm(value);
  if (!v) return { bg: "transparent", text: "inherit" };

  let hash = 0;
  for (let i = 0; i < v.length; i += 1) {
    hash = v.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return { bg: hslToHex(hue, 62, 93), text: hslToHex(hue, 55, 26) };
};

// --- Priority -> { bg, text, variant } -------------------------------------
export const getPriorityColors = (value) => {
  const v = norm(value) || "medium";
  if (v === "high" || v === "critical")
    return { bg: "#ffe5e5", text: "#d32f2f", variant: "danger" };
  if (v === "medium")
    return { bg: "#fff8e1", text: "#f57c00", variant: "warning" };
  return { bg: "#e8f5e9", text: "#2e7d32", variant: "success" }; // low / default
};

// --- IsUpdate state -> { bg, text } ----------------------------------------
export const getUpdateColors = (value) => {
  const v = norm(value);
  if (v === "backend work") return { bg: "#f3e5f5", text: "#7b1fa2" };
  if (v === "update") return { bg: "#e8f5e9", text: "#2e7d32" };
  if (v === "not update") return { bg: "#fff3e0", text: "#e65100" };
  return { bg: "#f8f9fa", text: "#6c757d" };
};

// --- Assignment state -> { bg, text, border } ------------------------------
export const getAssignmentColors = (value) => {
  const v = norm(value) || "unassigned";
  if (v === "assigned")
    return { bg: "#fff8e1", text: "#f57c00", border: "1px solid #b7921b" };
  return { bg: "#ffe5e5", text: "#d32f2f", border: "1px solid #ffe5e5" };
};

// --- Team -> { bg, text } --------------------------------------------------
// One colour per team, held here rather than in each page, so a task reads as
// the same team wherever it appears — grid, move dialog, or history trail.
// Keyed by name so the colour survives the team codes being different between
// the live database and a restored copy.
export const getTeamColors = (value) => {
  const v = norm(value);
  if (v.startsWith("database") || v === "db")
    return { bg: "#e3f2fd", text: "#1565c0" };
  if (v.startsWith("web") || v === "webapp")
    return { bg: "#ede7f6", text: "#5e35b1" };
  if (v.startsWith("test") || v === "qc")
    return { bg: "#e0f2f1", text: "#00695c" };
  if (v.startsWith("support") || v === "sup")
    return { bg: "#fff3e0", text: "#e65100" };
  return { bg: "#f1f3f5", text: "#6c757d" }; // no team yet — needs triage
};

// --- Report columns -> a colour function -----------------------------------
/* Which report columns get a background, and where that colour comes from.
   The columns that carry a small fixed set of values get one so a report can
   be read by scanning rather than word by word — which of these hundreds of
   rows are bugs, which are still Not Started, which came in as High.

   Only these. Tinting everything leaves nothing standing out, and a page of
   colour is harder to read than a page of none; free text, names, dates and
   the summed numbers all stay plain.

   Declared once and read by the Detailed Query Report on screen, the User
   Query Report, and the PDF both of them export — so a downloaded file cannot
   disagree with the report it came from. Keyed by column key, and the same
   value is spelled differently by different screens (isUpdate / IsUpdate,
   IsSupportTeam / SupportTeam), hence the aliases. */
export const REPORT_CELL_COLORS = {
  Client_UserName: getNameTint,
  AssigneeName: getNameTint,
  UserName: getNameTint,
  TeamName: getTeamColors,
  QueryType: getQueryTypeColors,
  /* "None" is stored as a literal string on a handful of rows and means the
     same as empty. getUpdateColors greys it, which is right for the editable
     pill on the task grid but would put a grey block in a column that is
     mostly nothing. */
  isUpdate: (v) =>
    String(v).trim().toLowerCase() === "none" ? null : getUpdateColors(v),
  IsUpdate: (v) =>
    String(v).trim().toLowerCase() === "none" ? null : getUpdateColors(v),
  Priority: getPriorityColors,
  Status: getStatusColors,
  // Only "Yes" is tinted — see getYesNoColors.
  isManuallyRaised: getYesNoColors,
  IsSupportTeam: getYesNoColors,
  SupportTeam: getYesNoColors,
};

// --- Handover action -> { icon, text, label } ------------------------------
// Forward and Return must not look alike: sending work back is the event
// somebody will be looking for when they scan a task's history.
export const getHandoverLook = (action) => {
  const v = norm(action);
  if (v === "return")
    return { icon: "bi-arrow-return-left", text: "#d32f2f", label: "Sent back" };
  if (v === "forward")
    return { icon: "bi-arrow-right-circle", text: "#2e7d32", label: "Moved" };
  if (v === "reassign")
    return { icon: "bi-people", text: "#1565c0", label: "Reassigned" };
  if (v === "teamcorrected")
    return { icon: "bi-pencil-square", text: "#e65100", label: "Team corrected" };
  if (v === "created")
    return { icon: "bi-plus-circle", text: "#5e35b1", label: "Raised" };
  return { icon: "bi-person-check", text: "#1565c0", label: "Assigned" };
};
